import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios, { setAuthProcessing } from '../lib/axios'

interface User {
  id: string
  name: string
  email: string
  picture?: string
  username?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => void
  logout: () => void
  getAccessToken: () => Promise<string | undefined>
}

const BackendAuthContext = createContext<AuthContextType | undefined>(undefined)

export function BackendAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessingAuth, setIsProcessingAuth] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const processedAuthRef = useRef<string | null>(null)

  // Check for auth parameter on initial load
  useEffect(() => {
    if (window.location.search.includes('auth=')) {
      console.log('🔐 Auth parameter detected on mount - setting auth processing')
      setAuthProcessing(5000)
    }
  }, [])

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const response = await axios.get('/auth/me')
      
      const userData = response.data
      console.log('🔐 Auth check successful:', userData.email)
      setUser({
        id: userData.id,
        name: userData.name,
        email: userData.email,
        picture: userData.picture,
        username: userData.nickname || userData.email?.split('@')[0] || ''
      })
      return true
    } catch (error) {
      // User is not authenticated
      console.log('🔐 Auth check failed:', error)
      setUser(null)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const login = () => {
    // Redirect to backend auth endpoint with current path
    const apiUrl = import.meta.env.VITE_API_URL || (
      import.meta.env.PROD 
        ? '/auth'  // Use relative path in production (routed by CloudFront)
        : 'http://localhost:3000/auth'  // Direct backend in development
    )
    const currentPath = window.location.pathname + window.location.search
    window.location.href = `${apiUrl}/login?return_to=${encodeURIComponent(currentPath)}`
  }

  const logout = () => {
    // Redirect to backend logout endpoint
    const apiUrl = import.meta.env.VITE_API_URL || (
      import.meta.env.PROD 
        ? '/auth'  // Use relative path in production (routed by CloudFront)
        : 'http://localhost:3000/auth'  // Direct backend in development
    )
    window.location.href = `${apiUrl}/logout`
  }

  const getAccessToken = async (): Promise<string | undefined> => {
    try {
      const response = await axios.get('/auth/token')
      return response.data.access_token
    } catch (error) {
      console.error('Error getting access token:', error)
      return undefined
    }
  }

  // Handle auth callback from Auth0
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const authStatus = params.get('auth')
    
    if (!authStatus) {
      return
    }

    // Create a unique key for this auth callback
    const authKey = `${location.pathname}-${authStatus}`
    
    // Check if we've already processed this exact auth callback
    if (processedAuthRef.current === authKey) {
      console.log('🔐 Auth callback already processed, skipping')
      return
    }

    console.log('🔐 Auth callback detected:', {
      authStatus,
      pathname: location.pathname,
      search: location.search
    })

    // Mark this auth callback as processed
    processedAuthRef.current = authKey
    setIsProcessingAuth(true)
    
    // Set auth processing flag to prevent redirects for 5 seconds
    setAuthProcessing(5000)

    // Process the auth callback
    if (authStatus === 'success') {
      // Wait longer for session to be fully established
      setTimeout(async () => {
        console.log('🔐 Checking auth after callback...')
        const authSuccess = await checkAuth()
        setIsProcessingAuth(false)
        
        // Only remove the auth parameter after we confirm auth worked
        if (authSuccess) {
          console.log('✅ Authentication successful, cleaning URL')
          navigate(location.pathname, { replace: true })
        } else {
          console.error('❌ Authentication check failed after callback')
          // Still clean URL to prevent loops
          navigate(location.pathname, { replace: true })
        }
      }, 2000) // Increased timeout to 2 seconds
    } else if (authStatus === 'error') {
      console.error('Authentication failed')
      setIsProcessingAuth(false)
      // Remove the auth parameter immediately on error
      navigate(location.pathname, { replace: true })
    }
  }, [location.search, location.pathname, navigate, checkAuth])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading: isLoading || isProcessingAuth,
    login,
    logout,
    getAccessToken,
  }

  return <BackendAuthContext.Provider value={value}>{children}</BackendAuthContext.Provider>
}

export function useBackendAuth() {
  const context = useContext(BackendAuthContext)
  if (context === undefined) {
    throw new Error('useBackendAuth must be used within a BackendAuthProvider')
  }
  return context
}
